import { useToast } from '@cian/ui-kit/toast';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { requestAuthentication } from '../../actions/authentication/requestAuthentication';
import {
  ADD_TO_COMPARISON,
  DELETE_FROM_COMPARISON,
  IAddToComparison,
  IDeleteFromComparison,
} from '../../constants/actionTypes/offersComparison';
import { NEWBUILDING_COMPARE_ENABLED_CONFIG } from '../../constants/newbuildingCompare';
import { NewbuildingComparisonButton } from '../../components/NewbuildingComparisonButton';
import {
  EStatus,
  IComparisonStatusInfoSchema,
} from '../../repositories/favorites/entities/comparison_status_info/ComparisonStatusInfoSchema';
import { selectUser } from '../../selectors/user';
import {
  addNewbuildingToComparison,
  type IComparisonActionResult,
  removeNewbuildingFromComparison,
} from '../../services/propertyComparison';
import { useApplicationContext } from '../../utils/applicationContext';
import { CompareToastContent } from './CompareToastContent';
import {
  trackAddNewbuildingToComparison,
  trackClickAddComparisonPopup,
  trackClickRemoveComparisonPopup,
  trackDeleteNewbuildingFromComparison,
} from './tracking';

const COMPARISON_TOAST_HIDE_DELAY = 5000;
const COMPARISON_ERROR_TEXT = 'Не удалось изменить сравнение. Попробуйте еще раз';

interface INewbuildingComparisonButtonContainerProps {
  entityId: number;
  label: string;
  comparisonStatus: IComparisonStatusInfoSchema;
}

export const NewbuildingComparisonButtonContainer: React.FC<INewbuildingComparisonButtonContainerProps> = ({
  entityId,
  label,
  comparisonStatus,
}) => {
  const { config, httpApi, logger } = useApplicationContext();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const toast = useToast({ placement: 'bottom-right', hideDelay: COMPARISON_TOAST_HIDE_DELAY, size: 'L' });
  const isNewbuildingCompareEnabled = config.get<boolean>(NEWBUILDING_COMPARE_ENABLED_CONFIG) || false;

  const isAdded = comparisonStatus.status === EStatus.Added;
  const isUnavailable = comparisonStatus.status === EStatus.Unavailable;

  const showComparisonToast = React.useCallback(
    (result: IComparisonActionResult) => {
      toast.open({
        type: result.success === false ? 'error' : 'success',
        content: (
          <CompareToastContent
            text={result.text}
            comparisonUrl={result.comparisonUrl}
            onClick={() => trackClickAddComparisonPopup(entityId)}
          />
        ),
        onClose: () => trackClickRemoveComparisonPopup(entityId),
      });
    },
    [toast],
  );

  const handleClick = React.useCallback(async () => {
    if (!user.isAuthenticated) {
      dispatch(requestAuthentication());

      return;
    }

    dispatch<IAddToComparison | IDeleteFromComparison>(
      isAdded ? { type: DELETE_FROM_COMPARISON, offerId: entityId } : { type: ADD_TO_COMPARISON, offerId: entityId },
    );

    try {
      const result = isAdded
        ? await removeNewbuildingFromComparison({ httpApi, logger, newbuildingId: entityId })
        : await addNewbuildingToComparison({ httpApi, logger, newbuildingId: entityId });

      showComparisonToast(result);

      if (result.success === false) {
        dispatch<IAddToComparison | IDeleteFromComparison>(
          isAdded
            ? { type: ADD_TO_COMPARISON, offerId: entityId }
            : { type: DELETE_FROM_COMPARISON, offerId: entityId },
        );

        return;
      }

      if (isAdded) {
        trackDeleteNewbuildingFromComparison({ entityId, label });
      } else {
        trackAddNewbuildingToComparison({ entityId, label });
      }
    } catch {
      dispatch<IAddToComparison | IDeleteFromComparison>(
        isAdded ? { type: ADD_TO_COMPARISON, offerId: entityId } : { type: DELETE_FROM_COMPARISON, offerId: entityId },
      );
      showComparisonToast({ success: false, text: COMPARISON_ERROR_TEXT });
    }
  }, [dispatch, entityId, httpApi, isAdded, label, logger, showComparisonToast, user.isAuthenticated]);

  if (!isNewbuildingCompareEnabled || isUnavailable) {
    return null;
  }

  return <NewbuildingComparisonButton isAdded={isAdded} onClick={handleClick} />;
};
